/** Element factories, style defaults, and the one place versions get bumped. */

import type {
  ArrowElement,
  BoxElement,
  FillStyle,
  FontFamily,
  FreedrawElement,
  Point,
  SlateElement,
  StrokeStyle,
  TextAlign,
  TextElement,
} from './types';

export interface ElementStyle {
  readonly strokeColor: string;
  readonly backgroundColor: string;
  readonly fillStyle: FillStyle;
  readonly strokeWidth: number;
  readonly strokeStyle: StrokeStyle;
  readonly opacity: number;
  readonly fontFamily: FontFamily;
  readonly fontSize: number;
  readonly textAlign: TextAlign;
}

/** Shapes are filled by default so they can be selected by clicking anywhere. */
export const DEFAULT_STYLE: ElementStyle = {
  strokeColor: '#1f2933',
  backgroundColor: '#dbeafe',
  fillStyle: 'solid',
  strokeWidth: 2,
  strokeStyle: 'solid',
  opacity: 1,
  fontFamily: 'sans',
  fontSize: 16,
  textAlign: 'left',
};

export const STROKE_PALETTE: readonly { value: string; label: string }[] = [
  { value: '#1f2933', label: 'Ink' },
  { value: '#b42318', label: 'Red' },
  { value: '#1570ef', label: 'Blue' },
  { value: '#027a48', label: 'Green' },
  { value: '#b54708', label: 'Amber' },
  { value: '#6941c6', label: 'Violet' },
];

export const FILL_PALETTE: readonly { value: string; label: string }[] = [
  { value: 'transparent', label: 'None' },
  { value: '#dbeafe', label: 'Blue tint' },
  { value: '#dcfae6', label: 'Green tint' },
  { value: '#fee4e2', label: 'Red tint' },
  { value: '#fef0c7', label: 'Amber tint' },
  { value: '#ece9fe', label: 'Violet tint' },
];

export interface EditMeta {
  readonly clientId: string;
  readonly now: number;
}

let counter = 0;

export function newId(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  counter += 1;
  return `id-${Date.now().toString(36)}-${counter.toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 8)}`;
}

function baseFrom(style: ElementStyle, meta: EditMeta) {
  return {
    id: newId(),
    angle: 0,
    strokeColor: style.strokeColor,
    backgroundColor: style.backgroundColor,
    fillStyle: style.backgroundColor === 'transparent' ? ('transparent' as const) : style.fillStyle,
    strokeWidth: style.strokeWidth,
    strokeStyle: style.strokeStyle,
    opacity: style.opacity,
    version: 1,
    updatedAt: meta.now,
    updatedBy: meta.clientId,
    isDeleted: false,
  };
}

export function createBox(
  type: BoxElement['type'],
  rect: { x: number; y: number; width: number; height: number },
  style: ElementStyle,
  meta: EditMeta,
): BoxElement {
  return Object.freeze({
    ...baseFrom(style, meta),
    type,
    x: rect.x,
    y: rect.y,
    width: Math.max(0, rect.width),
    height: Math.max(0, rect.height),
  });
}

export function createArrow(
  origin: { x: number; y: number },
  points: readonly Point[],
  style: ElementStyle,
  meta: EditMeta,
  opts: { startArrowhead?: boolean; endArrowhead?: boolean } = {},
): ArrowElement {
  const bounds = pointsExtent(points);
  return Object.freeze({
    ...baseFrom(style, meta),
    type: 'arrow' as const,
    x: origin.x,
    y: origin.y,
    width: bounds.width,
    height: bounds.height,
    // Arrows are never filled; only their heads are.
    fillStyle: 'transparent' as const,
    points: Object.freeze(points.slice()),
    startBinding: null,
    endBinding: null,
    startArrowhead: opts.startArrowhead ?? false,
    endArrowhead: opts.endArrowhead ?? true,
  });
}

export function createFreedraw(
  origin: { x: number; y: number },
  points: readonly Point[],
  style: ElementStyle,
  meta: EditMeta,
): FreedrawElement {
  const bounds = pointsExtent(points);
  return Object.freeze({
    ...baseFrom(style, meta),
    type: 'freedraw' as const,
    x: origin.x,
    y: origin.y,
    width: bounds.width,
    height: bounds.height,
    fillStyle: 'transparent' as const,
    points: Object.freeze(points.slice()),
  });
}

export function createText(
  origin: { x: number; y: number },
  text: string,
  style: ElementStyle,
  meta: EditMeta,
  opts: { containerId?: string | null; width?: number; height?: number; align?: TextAlign } = {},
): TextElement {
  return Object.freeze({
    ...baseFrom(style, meta),
    type: 'text' as const,
    x: origin.x,
    y: origin.y,
    width: opts.width ?? 0,
    height: opts.height ?? 0,
    fillStyle: 'transparent' as const,
    text,
    fontSize: style.fontSize,
    fontFamily: style.fontFamily,
    textAlign: opts.align ?? (opts.containerId ? 'center' : style.textAlign),
    containerId: opts.containerId ?? null,
  });
}

function pointsExtent(points: readonly Point[]): { width: number; height: number } {
  if (points.length === 0) return { width: 0, height: 0 };
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const [px, py] of points) {
    minX = Math.min(minX, px);
    minY = Math.min(minY, py);
    maxX = Math.max(maxX, px);
    maxY = Math.max(maxY, py);
  }
  return { width: maxX - minX, height: maxY - minY };
}

function sameValue(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (!sameValue(a[i], b[i])) return false;
    return true;
  }
  if (a && b && typeof a === 'object' && typeof b === 'object') {
    const ak = Object.keys(a as object);
    const bk = Object.keys(b as object);
    if (ak.length !== bk.length) return false;
    for (const k of ak) {
      if (!sameValue((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]))
        return false;
    }
    return true;
  }
  return false;
}

/**
 * Apply a patch and bump the version, but return the SAME object when nothing
 * actually changed.
 *
 * This is load bearing. A version bumped on a no-op would be broadcast to the
 * other tab, which would normalize, bump, and broadcast back, forever.
 */
export function updateElement<T extends SlateElement>(el: T, patch: Partial<T>, meta: EditMeta): T {
  let changed = false;
  for (const key of Object.keys(patch) as (keyof T)[]) {
    const next = patch[key];
    if (next === undefined) continue;
    if (!sameValue(el[key], next)) {
      changed = true;
      break;
    }
  }
  if (!changed) return el;
  const clean: Partial<T> = {};
  for (const key of Object.keys(patch) as (keyof T)[]) {
    if (patch[key] !== undefined) clean[key] = patch[key];
  }
  return Object.freeze({
    ...el,
    ...clean,
    version: el.version + 1,
    updatedAt: meta.now,
    updatedBy: meta.clientId,
  }) as unknown as T;
}

/** Deep-ish equality over the parts of an element that describe its appearance. */
export function elementsEqual(a: SlateElement, b: SlateElement): boolean {
  if (a === b) return true;
  const keys = Object.keys(a) as (keyof SlateElement)[];
  for (const k of keys) {
    if (k === 'version' || k === 'updatedAt' || k === 'updatedBy') continue;
    if (!sameValue(a[k], (b as unknown as Record<string, unknown>)[k as string])) return false;
  }
  return true;
}

/** A copy with a fresh id, offset for paste/duplicate. */
export function duplicateElement<T extends SlateElement>(
  el: T,
  offset: { dx: number; dy: number },
  meta: EditMeta,
  idMap?: Map<string, string>,
): T {
  const id = newId();
  idMap?.set(el.id, id);
  return Object.freeze({
    ...el,
    id,
    x: el.x + offset.dx,
    y: el.y + offset.dy,
    version: 1,
    updatedAt: meta.now,
    updatedBy: meta.clientId,
  }) as unknown as T;
}
